import { useEffect, useRef } from 'react';

import { getGoogleConsentState, updateGoogleConsent } from './googleConsentMode';

interface PixelProps {
  pixelId: string;
  marketingCookies: boolean;
  consentModeEnabled: boolean;
}

const GA4Pixel = ({ pixelId, marketingCookies, consentModeEnabled }: PixelProps) => {
  const loaderScriptRef = useRef<HTMLScriptElement | null>(null);
  const configScriptRef = useRef<HTMLScriptElement | null>(null);
  const idleCallbackIdRef = useRef<number | undefined>(undefined);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const marketingCookiesRef = useRef(marketingCookies);
  const lastConsentRef = useRef<boolean | undefined>(undefined);
  const trimmedId = pixelId?.trim();
  const validPixelId = Boolean(trimmedId?.match(/^G-[A-Z0-9]{8,}$/));
  const shouldLoadPixel = consentModeEnabled || marketingCookies;

  marketingCookiesRef.current = marketingCookies;

  useEffect(() => {
    if (!consentModeEnabled || !validPixelId || !trimmedId) {
      lastConsentRef.current = undefined;
      return;
    }

    if (document.getElementById('ga-pixel')) {
      return;
    }

    const initialMarketingConsent = marketingCookiesRef.current;
    const script = document.createElement('script');
    script.id = 'ga-pixel';
    script.innerHTML = `
      (function(){try{
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('consent', 'default', ${JSON.stringify(getGoogleConsentState(false))});
      gtag('consent', 'update', ${JSON.stringify(getGoogleConsentState(initialMarketingConsent))});
      gtag('js', new Date());
      gtag('config', '${trimmedId}', {
        page_path: window.location.pathname,
      });
      }catch{/*swallow exception*/}})();
    `;
    document.head.appendChild(script);
    lastConsentRef.current = initialMarketingConsent;

    return () => {
      script.remove();
      lastConsentRef.current = undefined;
    };
  }, [consentModeEnabled, trimmedId, validPixelId]);

  useEffect(() => {
    if (!consentModeEnabled || !validPixelId || lastConsentRef.current === marketingCookies) {
      return;
    }

    updateGoogleConsent(marketingCookies);
    lastConsentRef.current = marketingCookies;
  }, [consentModeEnabled, marketingCookies, validPixelId]);

  useEffect(() => {
    let isMounted = true;

    const loadPixel = () => {
      if (!isMounted || !validPixelId || !trimmedId || !shouldLoadPixel) {
        return;
      }

      try {
        if (document.getElementById('ga-pixel-loader')) {
          return;
        }

        const loaderScript = document.createElement('script');
        loaderScript.id = 'ga-pixel-loader';
        loaderScript.async = true;
        loaderScript.src = `https://www.googletagmanager.com/gtag/js?id=${trimmedId}`;
        document.head.appendChild(loaderScript);
        loaderScriptRef.current = loaderScript;

        if (!consentModeEnabled) {
          const configScript = document.createElement('script');
          configScript.async = true;
          configScript.id = 'ga-pixel';
          configScript.innerHTML = `
            (function(){try{
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${trimmedId}', {
              page_path: window.location.pathname,
            });
            }catch{/*swallow exception*/}})();
          `;
          document.head.appendChild(configScript);
          configScriptRef.current = configScript;
        }
      } catch {
        /* swallow exception */
      }
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleCallbackIdRef.current = window.requestIdleCallback(loadPixel, { timeout: 2000 });
    } else {
      timeoutIdRef.current = setTimeout(loadPixel, 100);
    }

    return () => {
      isMounted = false;

      if (idleCallbackIdRef.current !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackIdRef.current);
      }
      if (timeoutIdRef.current !== undefined) {
        clearTimeout(timeoutIdRef.current);
      }

      loaderScriptRef.current?.remove();
      loaderScriptRef.current = null;
      configScriptRef.current?.remove();
      configScriptRef.current = null;
    };
  }, [consentModeEnabled, shouldLoadPixel, trimmedId, validPixelId]);

  return null;
};

export default GA4Pixel;
